import { useRef } from 'react';
import type { ChangeEvent } from 'react';
import ArrowDown from '@engineering/icons/arrow-down';
import { SecondaryText, Ui } from '@silvertours/front-shared';

export type TimePickerType = 'from' | 'to';

type TimePickerProps = {
  type: TimePickerType;
  value?: string;
  onChange?: (time: string, type: TimePickerType) => void;
  pickupTimeLabel: string;
  pickupTimeText: string;
  returnTimeLabel: string;
  returnTimeText: string;
};

type Option = {
  label: string;
  value: string;
};

const generateTimeRange = () => {
  const options: Option[] = [];
  for (let m = 0; m < 1440; m += 15) {
    const time = `${Math.floor(m / 60)
      .toString()
      .padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}`;
    options.push({
      label: time,
      value: time,
    });
  }
  return options;
};

const TimePicker = ({
  type,
  onChange,
  pickupTimeText,
  pickupTimeLabel,
  returnTimeText,
  returnTimeLabel,
  value,
}: TimePickerProps) => {
  const output = useRef<HTMLOutputElement>(null);

  const timeOptions = generateTimeRange();

  const content = {
    from: {
      label: pickupTimeLabel,
      value: pickupTimeText,
    },
    to: {
      label: returnTimeLabel,
      value: returnTimeText,
    },
  };

  const setValue = (newValue?: string) => `${content[type].value} ${newValue}`;

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value: selectedValue } = event.target;
    if (output.current?.firstChild) {
      output.current.firstChild.textContent = setValue(selectedValue);
    }

    if (typeof onChange !== 'undefined') {
      onChange(selectedValue, type);
    }
  };

  return (
    <span className="color-primary hover:bg-color8 relative flex items-center gap-10 rounded-sm p-10">
      <span className="absolute bottom-0 left-0 right-0 top-0 w-full p-0 opacity-0">
        <Ui.Dropdown
          design="compact"
          id={`${type}TimeDropdown`}
          label={content[type].label}
          options={timeOptions}
          onChange={handleChange}
          value={value}
        />
      </span>
      <output ref={output}>
        <SecondaryText inline>{setValue(value)}</SecondaryText>
      </output>
      <ArrowDown className="h-30 w-30 fill-current" />
    </span>
  );
};

export { TimePicker };
